import { Zap } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { JackpotView } from "@/campaigns/modules/jackpot/types";
import { Badge } from "@/components/ui/badge";

import { countdownLabel } from "./countdown-label";

const HOUR_MS = 60 * 60 * 1000;

/**
 * A jackpot's boost window, as the legacy overlay showed it under the pot: while a boost runs,
 * "+50% boost" in the prize gold, with the time it has left once that is under an hour; before
 * one, a grey "Next boost in 2h 15m". Nothing when the pot has no boost scheduled. The flags and
 * dates ride the `jackpot_progress` stream; only the countdown runs on the board's clock (`now`).
 */
export const JackpotBoostBadge = ({
  view,
  now,
  className,
}: {
  view: JackpotView;
  now: number;
  className?: string;
}) => {
  const { t } = useTranslation();
  const event = view.events?.active;
  const percent = Number(view.jackpot.boosted_chance ?? 0);
  if (!event || !(percent > 0)) return null;

  const bolt = <Zap strokeWidth={2.5} />;

  if (event.is_boosted_now) {
    const boost = t("jackpot.boostBadge", {
      percent: percent.toLocaleString(),
    });
    const endsIn = event.boost_end ? Date.parse(event.boost_end) - now : null;
    return (
      <Badge
        variant="tonal"
        color="prizes"
        size="sm"
        icon={bolt}
        className={className}
      >
        {endsIn !== null && endsIn > 0 && endsIn <= HOUR_MS
          ? `${boost} · ${t("jackpot.boostEndsIn", { time: countdownLabel(endsIn, t) })}`
          : boost}
      </Badge>
    );
  }

  const startsIn = event.next_boost_start
    ? Date.parse(event.next_boost_start) - now
    : null;
  if (startsIn === null || startsIn <= 0) return null;
  return (
    <Badge
      variant="ghost"
      color="neutral"
      size="sm"
      icon={bolt}
      className={className}
    >
      {t("jackpot.nextBoostIn", { time: countdownLabel(startsIn, t) })}
    </Badge>
  );
};
